import React, { useMemo, useState } from 'react';
import { Paper, Button, Typography, Box, CircularProgress, Alert } from '@mui/material';
import {
  VisibilityRounded, PaidRounded, ScheduleRounded, CreditCardOffRounded, WorkspacePremiumRounded,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { FilterBar, Field, SearchInput, SelectInput, Spacer, ResetButton, Grid } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import { usePayments, PaymentListItem } from '../../hooks/usePayments';
import { money, statusLabel } from '../../utils/format';
import { colors } from '../../theme/theme';

const PaymentsPage: React.FC = () => {
  const { items, summary, filters, loading, error, reload } = usePayments();
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');

  const rows = useMemo(
    () =>
      items.filter(
        (r) =>
          (!q || (r.business + r.invoice + r.id).toLowerCase().includes(q.toLowerCase())) &&
          (!plan || r.plan === plan) && (!status || r.status === status)
      ),
    [items, q, plan, status]
  );

  const columns: Column<PaymentListItem>[] = [
    { key: 'id', label: 'Payment', render: (r) => <Typography component="span" sx={{ fontWeight: 600, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.id.slice(0, 8)}</Typography> },
    { key: 'business', label: 'Business' },
    { key: 'plan', label: 'Plan' },
    { key: 'amount', label: 'Amount', align: 'right', csv: (r) => r.amount, render: (r) => money(r.amount) },
    { key: 'date', label: 'Date' },
    { key: 'invoice', label: 'Invoice', render: (r) => <Typography component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: colors.textTertiary }}>{r.invoice}</Typography> },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
    {
      key: '_', label: '', render: (r) => (
        <Button component={Link} to={`/payments/${r.id}`} size="small" variant="outlined" startIcon={<VisibilityRounded />}
          sx={{ borderColor: 'divider', color: 'text.primary' }}>View</Button>
      ),
    },
  ];

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error}</Alert>;
  }

  return (
    <>
      <Grid cols={4} sx={{ mb: 3 }}>
        <StatCard icon={<PaidRounded />} color="#16A34A" value={money(summary.collected)} label="Total Collected" />
        <StatCard icon={<ScheduleRounded />} color="#F59E0B" value={money(summary.pending)} label="Pending" />
        <StatCard icon={<CreditCardOffRounded />} color="#EF4444" value={summary.failed} label="Failed Payments" />
        <StatCard icon={<WorkspacePremiumRounded />} color="#076FF7" value={summary.active_subscriptions} label="Active Subscriptions" />
      </Grid>

      <FilterBar>
        <Field label="Search"><SearchInput value={q} onChange={setQ} placeholder="Business or invoice…" /></Field>
        <Field label="Plan"><SelectInput value={plan} onChange={setPlan} options={[{ value: '', label: 'All plans' }, ...filters.plans]} minWidth={150} /></Field>
        <Field label="Status"><SelectInput value={status} onChange={setStatus} options={[{ value: '', label: 'All statuses' }, ...filters.statuses.map((s) => ({ value: s, label: statusLabel(s) }))]} /></Field>
        <Spacer />
        <ResetButton onClick={() => { setQ(''); setPlan(''); setStatus(''); }} />
      </FilterBar>

      <Paper sx={{ p: 0 }}>
        <DataTable columns={columns} rows={rows} title="Payments" exportName="payments" />
      </Paper>
    </>
  );
};

export default PaymentsPage;
